import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "../lib/api";
import { useFetch } from "../lib/useFetch";
import { getSocket } from "../lib/socket";
import type { Ticket } from "../lib/types";

const STATUSES = ["open", "in_progress", "waiting", "resolved", "closed"];

export default function Tickets() {
  const [params, setParams] = useSearchParams();
  const status = params.get("status") ?? "";
  const clientId = params.get("clientId") ?? "";

  const qs = new URLSearchParams();
  if (status) qs.set("status", status);
  if (clientId) qs.set("clientId", clientId);
  const { data, error, reload } = useFetch<Ticket[]>(
    `/api/tickets?${qs.toString()}`,
  );

  const [busyId, setBusyId] = useState<string | null>(null);

  // Live-refresh when the server signals ticket activity.
  useEffect(() => {
    const socket = getSocket();
    const onChange = () => reload();
    socket.on("ticket:new", onChange);
    socket.on("ticket:updated", onChange);
    return () => {
      socket.off("ticket:new", onChange);
      socket.off("ticket:updated", onChange);
    };
  }, [reload]);

  async function setStatus(id: string, next: string) {
    setBusyId(id);
    try {
      await api(`/api/tickets/${id}`, { method: "PATCH", body: { status: next } });
      reload();
    } finally {
      setBusyId(null);
    }
  }

  function filterStatus(next: string) {
    const p = new URLSearchParams(params);
    if (next) p.set("status", next);
    else p.delete("status");
    setParams(p);
  }

  return (
    <>
      <div className="topbar">
        <h1>Tickets</h1>
        <div className="spacer" />
        <select
          style={{ width: 180 }}
          value={status}
          onChange={(e) => filterStatus(e.target.value)}
        >
          <option value="">All statuses</option>
          {STATUSES.map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </select>
      </div>

      {error && <div className="error">{error}</div>}

      <div className="card">
        <table>
          <thead>
            <tr>
              <th>#</th>
              <th>Subject</th>
              <th>Client</th>
              <th>Device</th>
              <th>Priority</th>
              <th>Source</th>
              <th>Assignee</th>
              <th>Updated</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {data?.map((t) => (
              <tr key={t.id}>
                <td className="muted">#{t.number}</td>
                <td>
                  {t.subject}
                  {t.messages && t.messages[0] && (
                    <div className="muted" style={{ fontSize: 12 }}>
                      {t.messages[0].body.slice(0, 80)}
                    </div>
                  )}
                </td>
                <td>{t.client?.name ?? "—"}</td>
                <td>
                  {t.device
                    ? `${t.device.hostname} (${t.device.username})`
                    : "—"}
                </td>
                <td>
                  <span
                    className={`pill ${
                      t.priority === "urgent" ? "danger" : ""
                    }`}
                  >
                    {t.priority}
                  </span>
                </td>
                <td className="muted">{t.source}</td>
                <td>{t.staff?.name ?? "—"}</td>
                <td className="muted">
                  {new Date(t.updatedAt).toLocaleString()}
                </td>
                <td>
                  <select
                    value={t.status}
                    disabled={busyId === t.id}
                    onChange={(e) => setStatus(t.id, e.target.value)}
                  >
                    {STATUSES.map((s) => (
                      <option key={s} value={s}>
                        {s}
                      </option>
                    ))}
                  </select>
                </td>
              </tr>
            ))}
            {data?.length === 0 && (
              <tr>
                <td colSpan={9} className="muted">
                  No tickets.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </>
  );
}
